import { useEffect, useRef, useState } from "react";
import { PaperPlaneTilt } from "@phosphor-icons/react";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { api, formatApiErrorDetail } from "@/lib/api";

const splitList = (s) =>
  String(s || "")
    .split(/[,;\s]+/)
    .map((x) => x.trim())
    .filter(Boolean);

// Staff edit the quote email (recipients, subject, message) and preview it before it goes out.
export const QuoteEmailDialog = ({ open, onOpenChange, quoteId, onSent }) => {
  const [f, setF] = useState(null);
  const [mode, setMode] = useState("edit");
  const [preview, setPreview] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const bodyRef = useRef(null);

  const payload = (cur) => ({
    to: splitList(cur.to),
    cc: splitList(cur.cc),
    subject: cur.subject,
    headline: cur.headline,
    body: cur.body,
    attach_pdf: cur.attach_pdf,
  });

  useEffect(() => {
    if (!open || !quoteId) return;
    setF(null);
    setError("");
    setMode("edit");
    api
      .post(`/quotes/${quoteId}/email/preview`, {})
      .then(({ data }) => {
        setF({
          to: data.to.join(", "),
          cc: data.cc.join(", "),
          subject: data.subject,
          headline: data.headline || "",
          body: data.body,
          attach_pdf: data.has_pdf,
          has_pdf: data.has_pdf,
          placeholders: data.placeholders,
          suggestions: data.suggestions || [],
        });
        setPreview(data.html);
      })
      .catch((e) => setError(formatApiErrorDetail(e) || "Could not load the email"));
  }, [open, quoteId]);

  useEffect(() => {
    if (!open || !f || mode !== "preview") return undefined;
    const t = setTimeout(() => {
      api
        .post(`/quotes/${quoteId}/email/preview`, payload(f))
        .then(({ data }) => setPreview(data.html))
        .catch(() => {});
    }, 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, open]);

  const insertTag = (tag) => {
    const el = bodyRef.current;
    const start = el?.selectionStart ?? f.body.length;
    const end = el?.selectionEnd ?? f.body.length;
    const body = f.body.slice(0, start) + tag + f.body.slice(end);
    setF({ ...f, body });
    requestAnimationFrame(() => {
      if (!el) return;
      el.focus();
      el.setSelectionRange(start + tag.length, start + tag.length);
    });
  };

  const send = async () => {
    if (!splitList(f.to).length) {
      setError("Add at least one recipient");
      return;
    }
    setBusy(true);
    setError("");
    try {
      const { data } = await api.post(`/quotes/${quoteId}/email`, payload(f));
      toast.success(`Quote emailed to ${data.to}${data.pdf ? " with the PDF attached" : ""}`);
      onOpenChange(false);
      onSent?.(data);
    } catch (e) {
      setError(formatApiErrorDetail(e) || "Could not send that email");
    }
    setBusy(false);
  };

  const field = "mt-1 border-zinc-800 bg-zinc-950";
  const label = "block text-xs font-semibold text-zinc-400";
  const has = (list, email) => splitList(list).some((x) => x.toLowerCase() === email.toLowerCase());
  const toggle = (key, email) => {
    const cur = splitList(f[key]);
    const next = has(f[key], email)
      ? cur.filter((x) => x.toLowerCase() !== email.toLowerCase())
      : [...cur, email];
    setF({ ...f, [key]: next.join(", ") });
  };
  const Chips = ({ field: key }) =>
    f.suggestions?.length ? (
      <div className="mt-1.5 flex flex-wrap gap-1" data-testid={`quote-email-${key}-options`}>
        {f.suggestions.map((s) => {
          const on = has(f[key], s.email);
          return (
            <button
              key={s.email}
              type="button"
              data-testid={`quote-email-${key}-option`}
              onClick={() => toggle(key, s.email)}
              title={s.email}
              className={`rounded-full border px-2 py-0.5 text-[11px] transition-colors duration-150 ${
                on
                  ? "border-amber-400/50 bg-amber-400/10 text-amber-200"
                  : "border-zinc-800 text-zinc-400 hover:border-zinc-600 hover:text-zinc-200"
              }`}
            >
              {on ? "✓ " : "+ "}
              {s.label}
            </button>
          );
        })}
      </div>
    ) : null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-h-[92vh] overflow-y-auto sm:max-w-3xl"
        data-testid="quote-email-dialog"
      >
        <DialogHeader>
          <DialogTitle>Send quote email</DialogTitle>
          <DialogDescription>
            Edit who gets it and what it says. Pricing, the portal button and the PDF are added
            automatically below your message.
          </DialogDescription>
        </DialogHeader>
        {!f && !error && <div className="py-8 text-sm text-zinc-500">Loading…</div>}
        {error && (
          <p className="text-sm text-red-400" data-testid="quote-email-error">
            {error}
          </p>
        )}
        {f && (
          <div className="space-y-3">
            <div className="grid gap-3 sm:grid-cols-2">
              <label className={label}>
                To
                <Input
                  data-testid="quote-email-to"
                  value={f.to}
                  onChange={(e) => setF({ ...f, to: e.target.value })}
                  placeholder="customer@email.com, second@email.com"
                  className={field}
                />
                <Chips field="to" />
              </label>
              <label className={label}>
                CC
                <Input
                  data-testid="quote-email-cc"
                  value={f.cc}
                  onChange={(e) => setF({ ...f, cc: e.target.value })}
                  placeholder="office@…"
                  className={field}
                />
                <Chips field="cc" />
              </label>
            </div>
            <label className={label}>
              Subject
              <Input
                data-testid="quote-email-subject"
                value={f.subject}
                onChange={(e) => setF({ ...f, subject: e.target.value })}
                className={field}
              />
            </label>
            <label className="flex items-center gap-2 text-xs text-zinc-300">
              <Checkbox
                data-testid="quote-email-attach-pdf"
                checked={f.attach_pdf}
                disabled={!f.has_pdf}
                onCheckedChange={(v) => setF({ ...f, attach_pdf: Boolean(v) })}
              />
              Attach the quote as a PDF
            </label>

            <div className="flex items-center justify-between">
              <span className={label}>Message</span>
              <div className="flex overflow-hidden rounded-md border border-zinc-700 text-[11px] font-semibold">
                {["edit", "preview"].map((m) => (
                  <button
                    key={m}
                    type="button"
                    data-testid={`quote-email-mode-${m}`}
                    onClick={() => setMode(m)}
                    className={`px-3 py-1 capitalize transition-colors duration-150 ${
                      mode === m ? "bg-zinc-100 text-zinc-900" : "text-zinc-400 hover:text-zinc-100"
                    }`}
                  >
                    {m}
                  </button>
                ))}
              </div>
            </div>

            {mode === "edit" ? (
              <div className="space-y-2">
                <Input
                  data-testid="quote-email-headline"
                  value={f.headline}
                  onChange={(e) => setF({ ...f, headline: e.target.value })}
                  placeholder="Headline (optional)"
                  className="border-zinc-800 bg-zinc-950 font-semibold"
                />
                <Textarea
                  ref={bodyRef}
                  data-testid="quote-email-body"
                  value={f.body}
                  onChange={(e) => setF({ ...f, body: e.target.value })}
                  rows={9}
                  className="border-zinc-800 bg-zinc-950 text-sm leading-relaxed"
                />
                <div className="text-[11px] text-zinc-500" data-testid="quote-email-placeholders">
                  <span className="mr-2 font-semibold text-zinc-400">Placeholders — click to insert:</span>
                  {f.placeholders.map((p) => (
                    <button
                      key={p.tag}
                      type="button"
                      data-testid={`quote-email-tag-${p.tag.replace(/[{}]/g, "")}`}
                      onClick={() => insertTag(p.tag)}
                      title={p.value}
                      className="mr-2 inline-flex items-baseline gap-1 rounded border border-zinc-800 px-1.5 py-0.5 font-mono-data hover:border-zinc-600"
                    >
                      {p.tag}
                      <span className="max-w-[140px] truncate text-zinc-600">{p.value}</span>
                    </button>
                  ))}
                </div>
              </div>
            ) : (
              <div className="overflow-hidden rounded-lg border border-zinc-800 bg-white">
                <iframe
                  title="Quote email preview"
                  data-testid="quote-email-preview"
                  srcDoc={preview}
                  className="h-[480px] w-full"
                />
              </div>
            )}

            <div className="flex items-center justify-end gap-2 pt-2">
              <Button
                variant="outline"
                data-testid="quote-email-cancel"
                onClick={() => onOpenChange(false)}
                className="border-zinc-700 bg-transparent"
              >
                Cancel
              </Button>
              <Button data-testid="quote-email-send" onClick={send} disabled={busy} className="gap-2">
                <PaperPlaneTilt size={16} weight="bold" />
                {busy ? "Sending…" : "Send email"}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
